import { X } from "lucide-react";
import { Badge } from "../ui/Badge";
import { cn } from "../../lib/utils";

interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

const LANGUAGES: LanguageOption[] = [
  { code: "it", label: "Italiano", flag: "\ud83c\uddee\ud83c\uddf9" },
  { code: "en", label: "English", flag: "\ud83c\uddec\ud83c\udde7" },
  { code: "fr", label: "Fran\u00e7ais", flag: "\ud83c\uddeb\ud83c\uddf7" },
  { code: "de", label: "Deutsch", flag: "\ud83c\udde9\ud83c\uddea" },
  { code: "es", label: "Espa\u00f1ol", flag: "\ud83c\uddea\ud83c\uddf8" },
];

interface LanguageSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  disabled?: boolean;
}

export function LanguageSelect({
  value = [],
  onChange,
  className,
  disabled = false,
}: LanguageSelectProps) {
  const toggleLanguage = (code: string) => {
    if (disabled) return;

    if (value.includes(code)) {
      onChange(value.filter((v) => v !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const getLanguage = (code: string) => LANGUAGES.find((l) => l.code === code);

  return (
    <div className={cn("space-y-2", className)}>
      {/* Language options */}
      <div className="flex flex-wrap gap-2">
        {LANGUAGES.map((lang) => {
          const isSelected = value.includes(lang.code);
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => toggleLanguage(lang.code)}
              disabled={disabled}
              className={cn(
                "px-3 py-1.5 rounded-lg border text-sm font-medium transition-all",
                isSelected
                  ? "border-[#284899] bg-[#284899]/10 text-[#284899]"
                  : "border-border bg-card hover:border-[#284899]/50 text-foreground/80",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <span className="mr-1.5">{lang.flag}</span>
              {lang.label}
            </button>
          );
        })}
      </div>

      {/* Selected languages badges */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map((code) => {
            const lang = getLanguage(code);
            if (!lang) return null;
            return (
              <Badge
                key={code}
                variant="default"
                className="gap-1 pr-1"
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => toggleLanguage(code)}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
